import { FundingPolicyType, checkPolicyCalculationTypeEquals } from '@shared';

import {
  isFiniteNumber,
  toNormalizedRate,
  formatPercentLabel,
  formatYearsLabel,
} from '@/features/policies-comparison';

export function buildEliminationPeriodLabel(
  homeCareWaitingDays?: number,
  facilityCareWaitingDays?: number,
  fundingPolicyType?: FundingPolicyType,
): string | undefined {
  if (!isFiniteNumber(homeCareWaitingDays) && !isFiniteNumber(facilityCareWaitingDays)) {
    return undefined;
  }

  const homeDays = isFiniteNumber(homeCareWaitingDays) ? homeCareWaitingDays : 0;
  const facilityDays = isFiniteNumber(facilityCareWaitingDays) ? facilityCareWaitingDays : 0;

  let label =
    homeDays === facilityDays
      ? `${homeDays} days`
      : `${homeDays} days home care / ${facilityDays} days facility care`;

  const isCareMattersPolicy =
    fundingPolicyType === FundingPolicyType.careMattersTwo ||
    fundingPolicyType === FundingPolicyType.jointCareMatters;

  if (isCareMattersPolicy) {
    label += ' (paid retroactively in month 4)';
  }

  return label;
}

export function buildGrowthLabel(
  fundingPolicySource: PolicyFundingSource,
  phaseCalculations: SinglePhaseCalculations[],
): string | undefined {
  const {
    policyInflationProtection,
    policyInflationProtectionOnFaceAmount,
    policyInflationProtectionOnCOBAmount,
    isSimpleInflationProtection,
    policyInflationProtectionYears,
    policyType,
  } = fundingPolicySource;

  const policyInflationProtectionRate = toNormalizedRate(policyInflationProtection);
  const inflationPeriodYears =
    phaseCalculations[0]?.inflationPeriodYears ?? policyInflationProtectionYears ?? 0;
  const inflationYearsLabel = inflationPeriodYears ? formatYearsLabel(inflationPeriodYears) : '';
  const simpleOrCompoundLabel = isSimpleInflationProtection ? 'Simple' : 'Compound';
  const inflationMethodAndDurationLabel = `${simpleOrCompoundLabel}${inflationYearsLabel ? ` (${inflationYearsLabel} yrs)` : ''}`;

  if (checkPolicyCalculationTypeEquals(policyType, [FundingPolicyType.annuityHybrid])) {
    return `Variable Growth ${inflationYearsLabel ? ` (${inflationYearsLabel} yrs)` : ''}`;
  }

  if (policyType === FundingPolicyType.nylAssetFlex) {
    const cobRate = policyInflationProtectionOnCOBAmount
      ? formatPercentLabel(policyInflationProtectionOnCOBAmount)
      : undefined;
    return cobRate ? `${cobRate} Compound (EOB only)` : 'None';
  }

  if (isFiniteNumber(policyInflationProtectionRate)) {
    return `${formatPercentLabel(policyInflationProtectionRate)} ${inflationMethodAndDurationLabel}`;
  }

  const faceInflationRateLabel = policyInflationProtectionOnFaceAmount
    ? formatPercentLabel(policyInflationProtectionOnFaceAmount)
    : undefined;
  const cobInflationRateLabel = policyInflationProtectionOnCOBAmount
    ? formatPercentLabel(policyInflationProtectionOnCOBAmount)
    : undefined;
  const labelPieces = [];
  if (faceInflationRateLabel) labelPieces.push(`${faceInflationRateLabel} Face`);
  if (cobInflationRateLabel) labelPieces.push(`${cobInflationRateLabel} COB`);
  return `${labelPieces.join(', ')}${labelPieces.length ? ' ' : ''}${inflationMethodAndDurationLabel}`;
}
